import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { lock } from "src/store/Authentication/authActions";

// Give the 401 → refresh cycle enough time to complete before locking.
// If the user is idle (no API calls), the timer fires and locks normally.
// If the user is active, the 401→refresh updates jwtToken in the store,
// which clears this timer and sets a new one for the next expiry.
const LOCK_BUFFER_MS = 30 * 1000;

const decodeJwtExp = (token: string): number | null => {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return typeof payload.exp === "number" ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
};

const useSessionExpiry = (
  isLogged: boolean,
  isLocked: boolean,
  jwtToken: string | undefined,
) => {
  const dispatch = useDispatch();

  useEffect(() => {
    if (!isLogged || isLocked || !jwtToken) return;

    const exp = decodeJwtExp(jwtToken);
    if (!exp) return;

    const delay = exp - Date.now();

    if (delay + LOCK_BUFFER_MS <= 0) {
      dispatch(lock());
      return;
    }

    const timer = setTimeout(() => dispatch(lock()), delay + LOCK_BUFFER_MS);
    return () => clearTimeout(timer);
  }, [jwtToken, isLogged, isLocked, dispatch]);
};

export default useSessionExpiry;
